// PositionForm.jsx
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import TextFieldInput from "../../forms/TextFieldInput";
import SelectFieldInput from "../../forms/SelectFieldInput";
import {
  createPosition,
  fetchPositionById,
  updatePosition,
} from "../../../services/Axios";

export default function PositionForm({ initialData = {}, mode, onSubmit }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { control, handleSubmit, reset, watch } = useForm();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      reset(initialData);
    }
  }, [initialData, reset]);

  // Fetch position data for edit mode
  React.useEffect(() => {
    if (mode === "edit" && id) {
      setLoading(true);
      fetchPositionById(id)
        .then((response) => {
          if (response) {
            reset(response);
          }
        })
        .catch((error) => {
          console.error("Error fetching position:", error);
          setError("Failed to fetch position data");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, mode, reset]);

  const handleFormSubmit = async (data) => {
    setLoading(true);
    setError(null);

    try {
      if (mode === "edit" && id) {
        // Update existing position
        await updatePosition(id, data);
        navigate("/SodPositions");
      } else if (mode === "create" || mode === "copy") {
        // Create new position
        await createPosition(data);
        navigate("/SodPositions");
      } else if (onSubmit) {
        // Fallback to custom onSubmit if provided
        await onSubmit(data);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response?.data) {
        // Handle different error response formats
        let errorMessage = "";

        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (typeof error.response.data === "object") {
          errorMessage = Object.entries(error.response.data)
            ?.map(([field, messages]) => {
              const messageText = Array.isArray(messages)
                ? messages.join(", ")
                : messages;
              return `${field}: ${messageText}`;
            })
            .join("\n");
        }

        setError(errorMessage || "An error occurred while saving the position");
      } else {
        setError("An unexpected error occurred while saving the position");
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldLabel = (field) => {
    const labelMap = {
      StrategyType: "Strategy Type",
      Strategy1: "Strategy 1",
      Symbol: "Symbol",
      Alias2: "Alias 2",
      Start: "Start Quantity",
      Local: "Currency",
      SecType: "Security Type",
      StartPrc: "Start Price",
      Ticker_Exchange_Code: "Ticker Exchange Code",
      BB_Ticker_Code: "Bloomberg Ticker Code",
      Price_Multiplier: "Price Multiplier",
      Position_Multiplier: "Position Multiplier",
      sec_type2: "Security Type 2",
    };
    return (
      labelMap[field] ||
      field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  return (
    <>
      <Box sx={{ p: 3, maxWidth: "1200px", mx: "auto" }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
              p: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {mode === "edit"
                ? "Edit Position"
                : mode === "copy"
                  ? "Copy Position"
                  : "Create Position"}
            </Typography>
          </Box>

          {/* Form Content */}
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <Grid container spacing={3}>
                {/* Row 1 - Basic Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.text.secondary }}
                  >
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Symbol"
                    label="Symbol"
                    placeholder="Enter position symbol"
                    required={true}
                    rules={{ required: "Symbol is required" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Alias2"
                    label="Alias 2"
                    placeholder="Enter alias"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Local"
                    label="Currency"
                    placeholder="Enter currency code"
                    defaultValue="USD"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="StrategyType"
                    label="Strategy Type"
                    required={true}
                    rules={{ required: "Strategy Type is required" }}
                    options={[
                      {
                        value: "Called or Tendered",
                        label: "Called or Tendered",
                      },
                      { value: "Cash Management", label: "Cash Management" },
                      {
                        value: "Convertible Arbitrage",
                        label: "Convertible Arbitrage",
                      },
                    {
                        value: "Credit Arbitrage",
                        label: "Credit Arbitrage",
                    },
                      { value: "Expected Call", label: "Expected Call" },
                      { value: "Market Hedge", label: "Market Hedge" },
                      { value: "Merger Arbitrage", label: "Merger Arbitrage" },
                      { value: "RED", label: "RED" },
                      { value: "SPAC", label: "SPAC" },
                      {
                        value: "Yield to Maturity",
                        label: "Yield to Maturity",
                      },
                      { value: "", label: "No Strategy" },
                    ]}
                  />
                </Grid>

                {/* Row 2 - Strategy and Security Configuration */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, mt: 2, color: theme.palette.text.secondary }}
                  >
                    Strategy and Security Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Strategy1"
                    label="Strategy 1"
                    placeholder="Enter strategy details"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="SecType"
                    label="Security Type"
                    required={true}
                    rules={{ required: "Security Type is required" }}
                    options={[
                      { value: "CASH", label: "CASH" },
                      { value: "BOND-CORP", label: "BOND-CORP" },
                      { value: "BOND-GOVT", label: "BOND-GOVT" },
                      { value: "BOND-CONV", label: "BOND-CONV" },
                      { value: "EQ-DOMESTIC", label: "EQ-DOMESTIC" },
                      { value: "EQ-CL", label: "EQ-CL" },
                      { value: "EQ-PT", label: "EQ-PT" },
                      { value: "EQ-DOM-SWAP", label: "EQ-DOM-SWAP" },
                      { value: "IDX-SWAP", label: "IDX-SWAP" },
                      { value: "EQ-INTL-SWAP", label: "EQ-INTL-SWAP" },
                      { value: "TRS-BANKDEBT", label: "TRS-BANKDEBT" },
                      { value: "BANKDEBT", label: "BANKDEBT" },
                      { value: "EQ-INTL", label: "EQ-INTL" },
                      { value: "EQ-INTL-SWP-MNR", label: "EQ-INTL-SWP-MNR" },
                      { value: "EQ-MINOR", label: "EQ-MINOR" },
                      { value: "BOND-CVT-SWAP", label: "BOND-CVT-SWAP" },
                      { value: "WARRANT", label: "WARRANT" },
                      { value: "RIGHT", label: "RIGHT" },
                      { value: "", label: "Undefined" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="sec_type2"
                    label="Security Type 2"
                    options={[
                      { value: "CASH", label: "CASH" },
                      { value: "Bond", label: "Bond" },
                      { value: "Convertible_Bond", label: "Convertible Bond" },
                      { value: "Option", label: "Option" },
                      { value: "Equity", label: "Equity" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Start"
                    label="Start Quantity"
                    placeholder="Enter start quantity"
                    type="number"
                  />
                </Grid>

                {/* Row 3 - Pricing and Multipliers */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, mt: 2, color: theme.palette.text.secondary }}
                  >
                    Pricing and Multipliers
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="StartPrc"
                    label="Start Price"
                    placeholder="Enter start price"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Price_Multiplier"
                    label="Price Multiplier"
                    placeholder="Enter price multiplier"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Position_Multiplier"
                    label="Position Multiplier"
                    placeholder="Enter position multiplier"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Ticker_Exchange_Code"
                    label="Ticker Exchange Code"
                    placeholder="Enter exchange code"
                  />
                </Grid>

                {/* Row 4 - Bloomberg Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, mt: 2, color: theme.palette.text.secondary }}
                  >
                    Bloomberg Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="BB_Ticker_Code"
                    label="Bloomberg Ticker"
                    placeholder="Enter Bloomberg ticker"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      type="submit"
                      size="large"
                      disabled={loading}
                      sx={{
                        minWidth: "200px",
                        backgroundColor: theme.palette.primary.dark,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      {loading
                        ? "Saving..."
                        : mode === "edit"
                          ? "Update Position"
                          : "Create Position"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/SodPositions")}
                      size="large"
                      disabled={loading}
                      sx={{
                        minWidth: "120px",
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setError(null)} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
